<template>
  <div class="my-gallery-list" v-if="workspace">
    <div class="title-and-description">
      <div class="level title-line">
        <h1 class="level-left">
          <BaseIcon v-if="isJps" :name="autoKoicon(workspace.id) as SVG_NAMES" class="title-icon" />
          <input
            v-model="workspaceName"
            :disabled="isOnline"
            class="jps-ul-input is-size-5 has-text-weight-bold"
          />
        </h1>
        <div class="level-right share-switch">
          <BaseToggleSwitch
            v-model="isOnline"
            label-position="left"
            @click="clickOnline"
            >{{ lss('オンラインで共有', 'Share online') }}</BaseToggleSwitch
          >
          <CreateOnlineWorkspaceModal ref="createOnlineWorkspaceModal" :workspace="workspace" />
        </div>
      </div>
      <div class="level description-line">
        <div class="level-left">
          <input
            v-model="workspaceDescription"
            class="jps-ul-input is-size-7"
            :disabled="isOnline"
            :placeholder="lss('説明', 'Description')"
          />
        </div>
        <div class="level-right online-info" v-if="isOnline && entry">
          <dl>
            <dt class="is-size-8 online-link">
              <span class="has-text-weight-bold"
                >{{ lss('ギャラリー番号', 'Gallery Number ') }}
              </span>
            </dt>
            <dd>{{ getOnlineID }}</dd>
            <dt class="is-size-8">
              <span class="has-text-weight-bold online-link"
                >{{ lss('有効期限', 'Expired at ') }}
              </span>
            </dt>
            <dd>
              {{ dateTime(entry.expire)
              }}<span v-if="!isJa">&nbsp;JST</span>
            </dd>
            <dt class="is-size-8 online-link">
              <span class="has-text-weight-bold">{{
                lss('リンク', 'URL')
              }}</span>
            </dt>
            <dd>
              {{ getOnlineUrl
              }}<UiButtonIcon
                class="jps-icon-button is-size-6"
                tag="button"
                arialabel="Copy"
                icon="mdi/clipboard-outline"
                icon-size="size-6"
                @click="copyUrl"
              />
            </dd>
          </dl>
        </div>
      </div>
    </div>

    <div class="search-result">
      <div class="display-mode">
        <UiIconRadioButton class="jps-icon-button" v-model="displayMode" value="card" id="my-gallery-workspace-card" icon="mdi/view-grid" arialabel="grid" />
        <UiIconRadioButton class="jps-icon-button" v-model="displayMode" value="table" id="my-gallery-workspace-table" icon="mdi/table" arialabel="table" />
      </div>

      <div class="list">
        <Transition name="fade">
          <UiMygalleryCurationGrid
            v-if="displayMode === 'card'"
            key="card"
            :curations="curations"
            :addGalleryMode="addGalleryMode"
            @select-gallery="selectGallery"
            @delete-gallery="deleteGallery"
            @add-gallery="addGallery"
            @change-add-gallery-mode="changeAddGalleryMode"
          />
          <UiMygalleryCurationTable
            v-else
            key="table"
            :curations="curations"
            :addGalleryMode="addGalleryMode"
            @select-gallery="selectGallery"
            @delete-gallery="deleteGallery"
            @add-gallery="addGallery"
            @change-add-gallery-mode="changeAddGalleryMode"
          />
        </Transition>
      </div>
      <UiConfirmModal ref="confirmModal" />
      <div class="online-cover" v-if="isOnline">
        <p>
          {{
            lss(
              '共有中のギャラリーを編集するには、入室する必要があります。',
              'To edit a gallery that is being shared, you need to enter the room.',
            )
          }}
        </p>
        <button class="jps-round-button jps-blue jps-large" @click="login()">
          {{ lss('入室して編集', 'Enter and edit') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { UiConfirmModal, CreateOnlineWorkspaceModal } from '#components'
import type { SVG_NAMES } from '~/components/icons'

const { lss, isJa, langPath } = useI18n()
const { isJps } = useServiceType()
const { hostUrl } = useSite()
const { setLoading } = useLoader()
const { snackbarMsg } = useSnackbar()
const router = useRouter()
const curationStore = useCurationStore()
const workspaceStore = useWorkspaceStore()
const workspaceManagerStore = useWorkspaceManagerStore()

const LSKEY_MODE = 'mygallery_workspace_display'

interface Props {
  workspace?: WorkspaceEntry | null
}
const props = withDefaults(defineProps<Props>(),{})

type DisplayModeTypes = 'card' | 'table'
type AddGalleryModeTypes = 'new' | 'template'
const displayMode: Ref<DisplayModeTypes> = ref('card')
const addGalleryMode: Ref<AddGalleryModeTypes> = ref('new')
watch(
  () => displayMode.value,
  () => lsSet(LSKEY_MODE, displayMode.value),
)
const initDisplayMode = () => {
  displayMode.value = lsGet(LSKEY_MODE, 'card')
}
onMounted(initDisplayMode)
onBeforeUpdate(initDisplayMode)

watch(
  () => props.workspace,
  () => sync(),
)

const changeAddGalleryMode = (value: AddGalleryModeTypes) => {
  addGalleryMode.value = value
}

const databaseChanged = (id: string, type: string) => {
  sync()
}

onBeforeMount(async () => {
  curationStore.addListener(databaseChanged)
  await sync()
})

onBeforeUnmount(() => {
  curationStore.removeListener(databaseChanged)
})

// ワークスペース
const workspaceName = computed({
  get: () => props.workspace.name,
  set: v => {
    props.workspace.name = v
    workspaceStore.updateWorkspace({ id: props.workspace.id, name: v })
  }
})

const workspaceDescription = computed({
  get: () => props.workspace.description,
  set: v => {
    props.workspace.description = v
    workspaceStore.updateWorkspace({ id: props.workspace.id, description: v })
  }
})

const isOnline = computed(() => {
  if (props.workspace) return props.workspace.onlineId != null
  else return false
})

const createOnlineWorkspaceModal = ref<InstanceType<typeof CreateOnlineWorkspaceModal>>()
const clickOnline = async  (e: MouseEvent) => {
  e.preventDefault()
  if (!isOnline.value) {
    try {
      const conf = await createOnlineWorkspaceModal.value.openAwait()
      if (!conf) return
      setLoading(true)
      //オンラインワークスペースを作成
      await workspaceStore.makeOnline(props.workspace.id, conf.keyHash)
    } catch (e) {
    } finally {
      setLoading(false)
    }
  } else {
    setLoading(true)
    try {
      const we = await workspaceStore.makeOffline(props.workspace.id)
      console.info(we)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }
}

const getOnlineID = computed(() => {
  if (isOnline.value) {
    return props.workspace.onlineId.split(/-/)[1]
  }
  return null
})

const getOnlineUrl = computed(() => {
  if (isOnline.value) {
    return hostUrl() + langPath(`/mygallery/online?wid=${getOnlineID.value}`)
  }
  return null
})

const copyUrl =  () => {
  navigator.clipboard.writeText(getOnlineUrl.value).then(() => {
    snackbarMsg({ ja: 'URLをコピーしました', en: 'URL copeid' })
  })
}

// ギャラリー
const curations: Ref<Curation[]> = ref([])
const entry: Ref<WorkspaceEntry> = ref(null)
const sync = async () => {
  if (props.workspace) {
    curations.value = (
      await curationStore.listGalleries(props.workspace.id)
    ).map((ce) => ce.c)
    entry.value = await workspaceStore.get(props.workspace.id)
  }
}

const confirmModal = ref<InstanceType<typeof UiConfirmModal>>()
const deleteGallery = async (id: string) => {
  try {
    const confirm = await confirmModal.value.openAwait({
      message: { ja: '削除しますか？', en: 'Will you delete?' }
    })
    if (!confirm) return
    await curationStore.deleteCuration(id)
  } catch (cancel) { }
}

const selectGallery = (c: Curation) => {
  workspaceManagerStore.activeGalleryId = c.id
}

const addGallery = async () => {
  const newId = await workspaceManagerStore.addPage()
  if (addGalleryMode.value === 'template') {
    workspaceManagerStore.activeGalleryId = newId
    await router.push(langPath('/mygallery/edit?import=template'))
  }
}

const login = () => {
  workspaceManagerStore.activeOnlineWorkspaceId = props.workspace.onlineId
  router.push(langPath('/mygallery/online'))
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.my-gallery-list {
  background-color: $jps-gray-50;
  min-height: 100%;
  padding: 2rem;

  .level {
    margin-bottom: 0;
  }

  .title-and-description {
    background: $white;
    padding: 1rem 1.75rem 1.75rem;
    border-radius: $global-radius;
    border: 1px solid $jps-gray-300;

    .title-icon {
      color: $jps-key-orange;
    }
  }

  .title-line {
    gap: 1rem;
    margin-bottom: 1.5rem;

    .level-left {
      flex-grow: 1;
      display: flex;
      align-items: center;
      input {
        margin-left: 10px;
      }
    }
  }

  .share-switch {
    padding: 0.25rem 0;
  }

  .description-line {
    align-items: flex-start;

    .level-left {
      padding-left: 2rem;
      flex-grow: 1;

      .jps-ul-input {
        max-width: 48rem;
      }
    }

    .online-info {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 0.5rem;

      dl {
        background: #fff;
        border-radius: 0.5rem;
        border: 1px solid $jps-gray-400;
        padding: 1rem;

        dt {
          margin-top: 0.25rem;
        }

        dd {
          margin-left: 2rem;
          display: inline-flex;
          align-items: center;
        }
      }

      button {
        margin-left: 0.25rem;
        color: $jps-key-blue;

        &:hover {
          color: $jps-key-blue-hover;
        }
      }
    }
  }

  .search-result {
    padding: 2rem 1rem 1rem;
    position: relative;

    .display-mode {
      display: flex;
      align-items: center;

      @include is-tablet {
        justify-content: flex-end;
      }
      .jps-active {
        color: $jps-key-blue;

        &:hover {
          color: $jps-key-blue-hover;
        }
      }
    }

    .add-gallery-button {
      margin: 0;
      width: 14rem;
      color: $jps-gray-900 !important;
      justify-content: center;
      text-align: center;

      &:hover:not(:disabled),
      &:active:not(:disabled),
      &:focus-visible:not(:disabled) {
        color: $jps-gray-900 !important;
      }
    }

    .jps-card {
      position: relative;
      background: $white;
      border: 1px solid $jps-gray-300;
      @include panel-shadow;

      .users {
        position: absolute;
        top: 0.25rem;
        left: 0.25rem;
        display: flex;
        z-index: 1;

        .user-icon {
          display: inline-block;
          border-radius: 10px;
          width: 0.75rem;
          height: 0.75rem;

          &:not(:first-child) {
            margin-left: 1px;
          }
        }
      }

      .jps-card-body {
      }

      .cur-tools {
        position: absolute;
        display: flex;
        gap: 1rem;
        bottom: 0;
        right: 0;
        left: 0;
        padding: 0.75rem;
        justify-content: flex-end;
        font-size: 0.75rem;

        a {
          display: flex;
          gap: 3px;
          align-items: center;
          font-weight: bold;
        }

        background: $white;
        border-top: 1px solid $jps-gray-200;
      }

      &.add-gallery {
        min-height: 310px;
        display: flex;
        padding: 1.5rem;
        gap: 3rem;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        .add-gallery-options {
          width: auto;
          display: flex;
          flex-direction: column;
          gap: 1rem;
          align-items: flex-start;
          margin-left: -0.5rem;

          :deep(.jps-radio) {
            width: 100%;
            justify-content: flex-start;
          }

          :deep(.jps-radio+.jps-radio) {
            margin-left: 0;
          }

          :deep(.jps-radio input[type="radio"]:checked+.check) {
            border-color: $jps-key-orange;
          }

          :deep(.jps-radio input[type="radio"]:checked+.check:before) {
            background-color: $jps-key-orange;
          }

          :deep(.jps-radio:hover .check) {
            border-color: $jps-key-orange;
          }

          :deep(.jps-radio:hover .check:before) {
            background-color: $jps-key-orange;
          }
        }

        button {
          width: 14rem;
          align-self: center;
          justify-content: center;
          text-align: center;
        }

        @include is-mobile {
          min-height: 240px;
          padding-top: 2rem;
          padding-bottom: 2rem;
          gap: 1rem;

          .add-gallery-options {
            gap: 0;
          }
        }
      }
    }

    .result-table {
      .add-gallery-row {
        margin-top: 1rem;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 1.5rem;
        flex-wrap: wrap;
        width: 100%;
      }

      .add-gallery-options {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: 1.5rem;
        margin-left: -0.5rem;

        :deep(.jps-radio) {
          width: auto;
        }

        :deep(.jps-radio+.jps-radio) {
          margin-left: 0;
        }

        :deep(.jps-radio input[type="radio"]:checked+.check) {
          border-color: $jps-key-orange;
        }

        :deep(.jps-radio input[type="radio"]:checked+.check:before) {
          background-color: $jps-key-orange;
        }

        :deep(.jps-radio:hover .check) {
          border-color: $jps-key-orange;
        }

        :deep(.jps-radio:hover .check:before) {
          background-color: $jps-key-orange;
        }
      }

      @include is-mobile {
        .add-gallery-options {
          gap: 0;
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-start;
          margin: 0 auto;
        }

        .add-gallery-row {
          justify-content: center;
        }
      }
    }

    .online-cover {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      background-color: rgba(256, 256, 256, 0.95);
      pointer-events: all;
      padding: 3rem;
      display: flex;
      flex-direction: column;
      align-items: center;

      /* justify-content: center; */
      > * {
        margin-top: 2rem;
      }
    }
  }

  .jps-tag-list {
    margin-top: 1rem;

    .jps-tag {
      padding-right: 0px;

      .round-count {
        border-radius: 999px;
        background-color: $jps-gray-300;
        color: $black;
        font-weight: bold;
        min-width: 20px;
        height: 20px;
        margin-top: -2px;
        margin-bottom: -2px;
        line-height: 20px;
        margin-left: 0.5rem;
        margin-right: 2px;
      }
    }
  }
}
</style>
